import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Wrench, Users, Package, BellRing, UserCog, LogOut, Settings, MoreHorizontal, Receipt, HardDrive, Monitor, FileSignature, Plus, Hammer } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/constants";

const NAV = [
  { to: "/", label: "Tableau de bord", icon: LayoutDashboard, id: "dashboard" },
  { to: "/calendrier", label: "Planning", icon: CalendarDays, id: "calendar" },
  { to: "/interventions", label: "Interventions", icon: Wrench, id: "interventions" },
  { to: "/atelier", label: "Atelier", icon: Hammer, id: "workshop" },
  { to: "/clients", label: "Clients", icon: Users, id: "clients" },
  { to: "/equipements", label: "Équipements", icon: Monitor, id: "equipment" },
  { to: "/contrats", label: "Contrats", icon: FileSignature, id: "contracts" },
  { to: "/commandes", label: "Commandes matériel", icon: Package, id: "orders" },
  { to: "/rappels", label: "Suivis & maintenance", icon: BellRing, id: "reminders" },
  { to: "/facturation", label: "Facturation", icon: Receipt, id: "billing", admin: true },
  { to: "/sauvegardes", label: "Sauvegardes", icon: HardDrive, id: "backups", admin: true },
  { to: "/equipe", label: "Équipe", icon: UserCog, id: "team" },
  { to: "/parametres", label: "Paramètres", icon: Settings, id: "settings" },
];

const Brand = () => (
  <div className="flex items-center gap-3 px-2">
    <img src="/logo-sm.png" alt="DTech42" className="h-11 w-11 rounded-lg border border-cyan-400/30 object-cover" />
    <div>
      <p className="font-display text-lg font-bold leading-none text-white">DTech42</p>
      <p className="font-mono-dt text-[10px] uppercase tracking-widest text-cyan-300/70">Interventions</p>
    </div>
  </div>
);

const NavItems = ({ onNavigate }) => {
  const { user } = useAuth();
  return (
  <nav className="mt-8 flex flex-col gap-1">
    {NAV.filter((n) => !n.admin || user?.role === "admin").map(({ to, label, icon: Icon, id }) => (
      <NavLink
        key={to}
        to={to}
        end={to === "/"}
        onClick={onNavigate}
        data-testid={`nav-${id}-link`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-150 ${
            isActive ? "bg-cyan-400/10 text-cyan-300 border-l-2 border-cyan-400" : "text-slate-300 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        <Icon className="h-4 w-4" />
        {label}
      </NavLink>
    ))}
  </nav>
  );
};

const UserBox = () => {
  const { user, logout } = useAuth();
  return (
    <div className="mt-auto rounded-lg border border-cyan-400/15 bg-white/5 p-3">
      <p className="truncate text-sm font-medium text-white" data-testid="current-user-name">{user?.name}</p>
      <p className="truncate text-xs text-slate-400">{ROLES[user?.role]}</p>
      <button onClick={logout} data-testid="logout-button" className="mt-2 flex items-center gap-2 text-xs text-slate-400 transition-colors hover:text-red-300">
        <LogOut className="h-3.5 w-3.5" /> Se déconnecter
      </button>
    </div>
  );
};

const SidebarBody = ({ onNavigate }) => (
  <div className="flex h-full flex-col p-5">
    <Brand />
    <NavItems onNavigate={onNavigate} />
    <UserBox />
  </div>
);

const BOTTOM = [NAV[0], NAV[1], null, NAV[2]];

const NewButton = () => (
  <NavLink to="/interventions/nouvelle" data-testid="bottom-nav-new" aria-label="Nouvelle intervention"
    className="flex min-h-[60px] flex-col items-center justify-center gap-1 text-[11px] text-cyan-200">
    <span className="-mt-5 flex h-12 w-12 items-center justify-center rounded-full bg-cyan-400 text-[#0A1630] shadow-[0_0_18px_rgba(0,210,255,0.45)]"><Plus className="h-6 w-6" /></span>
    Nouvelle
  </NavLink>
);

const BottomNav = ({ onMore }) => (
  <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-cyan-400/15 bg-[#0A1630]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur lg:hidden" data-testid="mobile-bottom-nav">
    {BOTTOM.map((n) => n ? (
      <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`bottom-nav-${n.id}`}
        className={({ isActive }) => `flex min-h-[60px] flex-col items-center justify-center gap-1 text-[11px] ${isActive ? "text-cyan-300" : "text-slate-400"}`}>
        <n.icon className="h-5 w-5" />
        {n.id === "dashboard" ? "Accueil" : n.label}
      </NavLink>
    ) : <NewButton key="new" />)}
    <button onClick={onMore} data-testid="mobile-menu-button" className="flex min-h-[60px] flex-col items-center justify-center gap-1 text-[11px] text-slate-400">
      <MoreHorizontal className="h-5 w-5" />Plus
    </button>
  </nav>
);

export default function Layout() {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex min-h-screen dt-dots">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-cyan-400/10 bg-[#0A1630]/95 lg:block">
        <SidebarBody />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center justify-between border-b border-cyan-400/10 bg-[#0A1630]/90 px-4 py-3 backdrop-blur lg:hidden">
          <Brand />
        </header>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="right" className="w-72 bg-[#0A1630] p-0 border-cyan-400/20">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SidebarBody onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <main className="mx-auto w-full max-w-7xl flex-1 space-y-6 p-4 pb-28 sm:p-6 sm:pb-28 lg:p-8">
          <Outlet />
        </main>
        <BottomNav onMore={() => setOpen(true)} />
      </div>
    </div>
  );
}
